<script lang="ts">
	//Services
	import { format } from '../../../../../services/i18n';
	//Helpers
	import { formatSnakeCaseToTitle } from '../../../../../helpers/utilities';
	//Design system components
	import { Container, Radio, Text } from '@Shelly/shelly-design-system';
	import { afterUpdate } from 'svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import SettingGuide from '../../../SettingGuide.svelte';
	//Types
	import type { InputOutputOptions } from './input_output_types';

	export let typeValue: string,
		initialStateValue: string,
		initialStateOptions: InputOutputOptions,
		modeValue: string,
		mandatoryUpdate: boolean,
		matchInputOnlyOnSwitchFollow = false;

	const setInitialState = async (_initial_state_value) => {
		initialStateValue = _initial_state_value;
	};

	let disabled = {};

	const isSwitchOnlyInitialStateBlocked = () => {
		if (matchInputOnlyOnSwitchFollow) {
			return !(typeValue === 'switch' && modeValue === 'follow');
		}
		return (
			typeValue === 'button' ||
			(typeValue === 'switch' && (modeValue === 'flip' || modeValue === 'detached'))
		);
	};

	$: {
		const nextDisabled = {};
		Object.entries(initialStateOptions).forEach((option, i) => {
			nextDisabled[i] = option?.[1]?.type !== typeValue && option?.[1]?.type !== 'any';
		});
		if (isSwitchOnlyInitialStateBlocked()) {
			Object.entries(initialStateOptions).forEach(([, opt], i) => {
				if (opt?.type === 'switch') {
					nextDisabled[i] = true;
				}
			});
		}
		disabled = nextDisabled;
	}

	afterUpdate(async () => {
		//Finally using afterUpdate because changing the type value causes dynamic changes which can't be handled in beforeUpdate
		let selectedTypeIndex = Object.keys(initialStateOptions).indexOf(initialStateValue);
		let isSelectedTypedDisabled = disabled[selectedTypeIndex];

		let availableIndexes = [];
		let firstAvailableOption;

		if (isSelectedTypedDisabled) {
			Object.entries(disabled).forEach(([key, value]) => {
				if (!value) {
					availableIndexes.push(key);
				}
			});
			firstAvailableOption = Object.keys(initialStateOptions)[availableIndexes[0]];
			setInitialState(firstAvailableOption);
		}
		const selectedSwitchOnlyBlocked =
			isSwitchOnlyInitialStateBlocked() &&
			initialStateOptions[initialStateValue]?.type === 'switch' &&
			!mandatoryUpdate;
		if (selectedSwitchOnlyBlocked) {
			setInitialState(Object.keys(initialStateOptions)[0]);
		}

		/**
		 * Note:
		 * Short description of switch and button mode combinations:
		 * #* 1. switch:
		 * #- relay types:
		 * #-- follow * #-- allowed initial state: on, off, restore_last,match_input
		 * #-- flip * #-- allowed initial state: on, off, restore_last
		 * #-- detached * #-- allowed initial state: on, off, restore_last
		 * #-- activate  * #--- allowed initial state: on, off, restore_last, match_input
		 * #* 2. button:
		 * #- relay types:
		 * #-- momentary
		 * #-- detached
		 * #-- activate * #-- For all relay types allowed initial state: on, off, restore_last
		 *
		 * this is covered in https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2529
		 */
	});
</script>

{#if $showNewLayout}
	<div>
		<Text class="text-bold mb-xs">
			{formatSnakeCaseToTitle($format('init_state_label'))}
		</Text>
		<SettingGuide
			text={$format('settings_guide_action_on_power_on', {
				default:
					'Choose what this device does when power comes back after a cut — turn on, stay off, or restore the last state.',
			})}
		/>
		{#each Object.entries(initialStateOptions) as option, i (option[0])}
			{#if !disabled[i]}
				<Radio
					size="m"
					bind:selected={initialStateValue}
					value={option[0]}
					class="mb-xs"
					on:click={() => setInitialState(option[0])}
					id={`radio-initial-state-${option[0]}`}
					info={option[1]?.description}
				>
					{option[1]?.title}
				</Radio>
			{/if}
		{/each}
	</div>
{:else}
	<Container type="base" class="mb-s" padding="xs">
		<Text class="text-bold mb-s">
			{formatSnakeCaseToTitle($format('init_state_label'))}
		</Text>
		<SettingGuide
			text={$format('settings_guide_action_on_power_on', {
				default:
					'Choose what this device does when power comes back after a cut — turn on, stay off, or restore the last state.',
			})}
		/>
		{#each Object.entries(initialStateOptions) as option, i (option[0])}
			<Radio
				size="m"
				bind:selected={initialStateValue}
				value={option[0]}
				disabled={disabled[i]}
				on:click={() => setInitialState(option[0])}
				id="radio-mu4aojh"
			>
				{option[1]?.text}
			</Radio>
		{/each}
	</Container>
{/if}
